import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export function PhotoLoadStatus({
  loadedCount,
  expectedCount,
  isLoading,
  showProgress = false,
  className
}: {
  loadedCount: number;
  expectedCount?: number;
  isLoading: boolean;
  showProgress?: boolean;
  className?: string;
}) {
  if (showProgress && !isLoading) {
    return null;
  }

  const hasExpected = typeof expectedCount === 'number';
  const isPartial = hasExpected && loadedCount < expectedCount;
  const label = isLoading || isPartial
    ? hasExpected
      ? `${loadedCount} of ${expectedCount} photos loaded`
      : 'Loading photos'
    : `${loadedCount} photos`;

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      {isLoading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-sea" aria-hidden="true" /> : null}
      {showProgress ? (
        <div
          className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-ink/10 sm:max-w-sm"
          role="progressbar"
          aria-label="Photos loaded"
          aria-valuemin={0}
          aria-valuemax={hasExpected ? expectedCount : undefined}
          aria-valuenow={hasExpected ? Math.min(loadedCount, expectedCount) : undefined}
        >
          <div
            className="h-full rounded-full bg-sea transition-[width] duration-300"
            style={{ width: hasExpected && expectedCount > 0 ? `${Math.min(100, (loadedCount / expectedCount) * 100)}%` : isLoading ? '35%' : '100%' }}
          />
        </div>
      ) : (
        <div>
          <p className="text-sm font-semibold text-ink">{label}</p>
          {isLoading ? <p className="text-sm text-ink/70">Fetching preview links.</p> : null}
        </div>
      )}
    </div>
  );
}

export function PhotoLoadingPanel({
  loadedCount,
  expectedCount,
  showReadyCount = true
}: {
  loadedCount: number;
  expectedCount?: number;
  showReadyCount?: boolean;
}) {
  const hasExpected = typeof expectedCount === 'number';
  return (
    <div className="flex min-h-64 flex-col items-center justify-center rounded-sm border border-dashed border-ink/15 bg-white/70 p-8 text-center">
      <Loader2 className="mb-3 h-8 w-8 animate-spin text-sea" aria-hidden="true" />
      <p className="font-medium text-ink">Loading photos</p>
      {showReadyCount ? <p className="mt-1 text-sm text-ink/70">{hasExpected ? `${loadedCount} of ${expectedCount} ready` : 'Preparing the gallery'}</p> : null}
    </div>
  );
}
